import { Channel, Order } from "@/services/backend/htz/orders/types/order";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import t from "@/lang/lang";
import { useEffect, useState } from "react";
import {
  PutRequest,
  useHtzOrderPutMutation,
} from "@/services/backend/htz/orders/api/order-api";
import { Label } from "@/shared/components/ui/label";
import { Textarea } from "@/shared/components/ui/textarea";
import { cn } from "@/shared/lib/utils";
import { useDebouncedMutation } from "@/shared/lib/debounce/debounce";
import { DatePicker } from "@/shared/components/date-picker";
import { useHtzOfferListQuery } from "@/services/backend/htz/offers/api/offer-api";
import { Input } from "@/shared/components/ui/input";
import { DocumentNumberById } from "@/services/backend/numbers/components/document-number";
import { useCpyCopyMutation } from "@/services/backend/cpy/api/cpy";
import { CpyName } from "@/services/backend/cpy/types/entity-names";
import { parseRTKQueryErrorToSonner } from "@/shared/components/errors/parse-r-t-k-query-error";
import { toast } from "sonner";
import { useRefEffect } from "@/shared/hooks/use-ref-effect";

interface Props {
  order: Order;
}

export function OrderCard({ order }: Props) {
  const [request, setRequest] = useState<PutRequest>({
    ...order,
  });
  const mutation = useHtzOrderPutMutation();
  useDebouncedMutation(request, setRequest, mutation, { ...order });

  useRefEffect(order.offerId, () => {
    setRequest(order);
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex">
          <span>{t("Auftragseingang")}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="grid gap-4 md:grid-cols-2">
        <OfferSelect order={request} />
        <Datepicker
          date={request.date}
          onDateChange={(date) => setRequest({ ...request, date })}
        />
        <ChannelSelect
          channel={request.channel}
          onChannelChange={(channel) => setRequest({ ...request, channel })}
        />
        <CustomerReference
          customerReference={request.customerReference}
          onCustomerReferenceChange={(customerReference) =>
            setRequest({ ...request, customerReference })
          }
        />
        <InternalComment
          internalComment={request.internalComment}
          onInternalCommentChange={(internalComment) =>
            setRequest({ ...request, internalComment })
          }
          className="md:col-span-2"
        />
      </CardContent>
    </Card>
  );
}

export function OrderCardSkeleton() {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex">
          <span>{t("Auftragseingang")}</span>
        </CardTitle>
      </CardHeader>
    </Card>
  );
}

function OfferSelect({ order }: { order: Order }) {
  const {
    data: list,
    isLoading,
    error,
  } = useHtzOfferListQuery({
    process: {
      active: order.processId !== null,
      values: [order.processId ?? "00000000-0000-0000-0000-000000000000"],
    },
    immutable: {
      active: true,
      values: [true],
    },
  });

  // We re-use the copy service to update the order.
  const [
    cpy,
    {
      isLoading: cpyIsLoading,
      error: cpyError,
      isSuccess: cpyIsSuccess,
      reset: cpyReset,
    },
  ] = useCpyCopyMutation();

  const doCpy = (offerId: string) => {
    if (cpyIsLoading) return;

    cpy({
      destinationEntityName: CpyName.HTZOrder,
      destinationId: order.id,
      sourceEntityName: CpyName.HTZOffer,
      sourceId: offerId,
    });
  };

  useEffect(() => {
    if (error) {
      toast(...parseRTKQueryErrorToSonner(error));
    }
    if (cpyError) {
      toast(...parseRTKQueryErrorToSonner(cpyError));
      cpyReset();
    }
  }, [cpyError, cpyReset, error]);

  useEffect(() => {
    if (cpyIsSuccess) {
      toast.success(t("Daten übernommen."));
    }
  });

  return (
    <div className="grid content-start gap-1.5">
      <Label>{t("Zu Angebot")}</Label>
      <Select
        value={order.offerId ?? undefined}
        onValueChange={doCpy}
        disabled={!list || isLoading || cpyIsLoading}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder={t("Angebot auswählen")} />
        </SelectTrigger>
        <SelectContent>
          {list?.items.map((offer) => (
            <SelectItem value={offer.id} key={offer.id}>
              <DocumentNumberById documentId={offer.id} />
            </SelectItem>
          ))}
          <SelectItem value="00000000-0000-0000-0000-000000000000">
            {t("Kein Angebot")}
          </SelectItem>
        </SelectContent>
      </Select>
      <div className="text-muted-foreground text-xs">
        {t(
          "Die Auswahl eines Angebots überschreibt die Leistungen des Auftragseingangs mit denen des Angebots.",
        )}
      </div>
    </div>
  );
}

interface DatepickerProps {
  date: string | null;
  onDateChange: (date: string | null) => void;
}

function Datepicker({ date, onDateChange }: DatepickerProps) {
  const formattedDate = date ? new Date(date) : undefined;
  const onFormattedDateChange = (d: Date | undefined) => {
    if (!d) {
      onDateChange(null);
      return;
    }

    // We want to add the current time to the date
    const dateTime = new Date();
    dateTime.setUTCFullYear(d.getUTCFullYear(), d.getMonth(), d.getDate());
    onDateChange(dateTime.toISOString());
  };

  return (
    <div className="grid content-start gap-1.5">
      <Label>{t("Eingangsdatum")}</Label>
      <DatePicker date={formattedDate} onDateChange={onFormattedDateChange} />
    </div>
  );
}

interface ChannelSelectProps {
  channel: Channel | null;
  onChannelChange: (channel: Channel | null) => void;
}

function ChannelSelect({ channel, onChannelChange }: ChannelSelectProps) {
  const channels = Object.entries(Channel).map((chan) => chan[1]);

  const onValueChange = (value: string | null) => {
    if (value === null) {
      onChannelChange(null);
    }

    onChannelChange(value as Channel);
  };

  return (
    <div className="grid content-start gap-1.5">
      <Label>{t("Kanal")}</Label>
      <Select value={channel ?? undefined} onValueChange={onValueChange}>
        <SelectTrigger className="w-full">
          <SelectValue placeholder={t("Kanal auswählen")} />
        </SelectTrigger>
        <SelectContent>
          {channels.map((chan) => (
            <SelectItem value={chan} key={chan}>
              {t(chan)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

interface CustomerReferenceProps {
  customerReference: string;
  onCustomerReferenceChange: (comment: string) => void;
  className?: string;
}

function CustomerReference({
  customerReference,
  onCustomerReferenceChange,
  className = "",
}: CustomerReferenceProps) {
  return (
    <div className={cn("grid content-start gap-1.5", className)}>
      <Label>{t("Referenz des Kunden")}</Label>
      <Input
        value={customerReference ?? ""}
        onChange={(e) => onCustomerReferenceChange(e.target.value)}
      />
      <div className="text-muted-foreground text-xs">
        {t("z.B. Bestellnummer, Auftragsnummer")}
      </div>
    </div>
  );
}

interface InternalCommentProps {
  internalComment: string;
  onInternalCommentChange: (internalComment: string) => void;
  className?: string;
}

function InternalComment({
  internalComment,
  onInternalCommentChange,
  className = "",
}: InternalCommentProps) {
  return (
    <div className={cn("grid content-start gap-1.5", className)}>
      <Label>{t("Interner Kommentar")}</Label>
      <Textarea
        value={internalComment ?? ""}
        onChange={(e) => onInternalCommentChange(e.target.value)}
      />
    </div>
  );
}
